"use client";

import Link from "next/link";
import { Component, useEffect, useRef, useState, type ReactNode } from "react";
import type { ApiRoute } from "../_lib/reference/api";
import type { RouteCode } from "../_lib/reference/route-samples";
import { PAGE_BUILD, pinned } from "./build";
import { RouteBody } from "./RouteBody";

/** One route's fields and code, as its group's static JSON holds them. */
export interface RouteData {
  route: ApiRoute;
  code: RouteCode;
}

/** A group's static JSON: the build that wrote it, and its routes by anchor. */
export interface GroupData {
  build: string | null;
  routes: Record<string, RouteData>;
}

/** Each group's JSON, fetched once per page load and shared by its routes; a failed fetch is tried again. */
const loads = new Map<string, Promise<GroupData>>();
function load(src: string): Promise<GroupData> {
  let loading = loads.get(src);
  if (!loading) {
    loading = fetch(pinned(src)).then(async (r) => {
      if (!r.ok) throw new Error(`${src}: HTTP ${r.status}`);
      const data = (await r.json()) as GroupData;
      // Another build's JSON may not fit this page's code: it is not read.
      if (data.build !== PAGE_BUILD())
        throw new Error(`${src}: from build ${data.build}`);
      return data;
    });
    loading.catch(() => loads.delete(src));
    loads.set(src, loading);
  }
  return loading;
}

/** What a reader sees when a route's fields cannot arrive: why, then the route's own page. */
function Failed({ href }: { href: string }) {
  return (
    <p className="lazy failed">
      Couldn&apos;t load this route&apos;s fields.{" "}
      <Link href={href}>Open its own page.</Link>
    </p>
  );
}

/** A route's fields that do not render (a JSON this page's code does not read) fail to the link, never the page. */
class Boundary extends Component<
  { href: string; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? (
      <Failed href={this.props.href} />
    ) : (
      this.props.children
    );
  }
}

/**
 * A route's fields and code, fetched from its group's static JSON the first time the route opens (a link to
 * it opens it too), with a quiet line while they come. A reader with no script gets a link to the route's own
 * page, which has all of it; so does a reader whose fetch fails, or whose JSON is another build's, with why.
 */
export function LazyRouteBody({
  src,
  anchor,
  href,
}: {
  src: string;
  anchor: string;
  href: string;
}) {
  const here = useRef<HTMLDivElement>(null);
  const [data, setData] = useState<RouteData | null>(null);
  const [state, setState] = useState<"closed" | "loading" | "failed">("closed");
  useEffect(() => {
    const route = here.current?.closest("details");
    if (!route) return;
    const want = () => {
      if (!route.open) return;
      setState("loading");
      load(src).then(
        (all) => {
          const found = all.routes?.[anchor];
          if (found) setData(found);
          else setState("failed");
        },
        () => setState("failed")
      );
    };
    want();
    route.addEventListener("toggle", want);
    return () => route.removeEventListener("toggle", want);
  }, [src, anchor]);
  if (data)
    return (
      <Boundary href={href}>
        <RouteBody route={data.route} code={data.code} />
      </Boundary>
    );
  return (
    <div ref={here} aria-busy={state === "loading" || undefined}>
      {state === "failed" ? (
        <Failed href={href} />
      ) : state === "loading" ? (
        <p className="lazy-wait">Loading the fields...</p>
      ) : null}
      {/* With no script: the route's own page, which has all of it. */}
      <div className="lazy">
        <Link href={href}>Every field it takes and answers, and its code</Link>
      </div>
    </div>
  );
}
